import { Injectable } from '@angular/core';
import {
  MerchandisePrices,
  MerchandiseService,
} from '@epicuro-next/data/merchandise';
import {
  ConfigurationsService,
  MerchandisePricesConfigurationKeys,
  NAMESPACE_MERCHANDISE_PRICES,
} from '@epicuro-next/platform/configuration';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  throttleTime,
} from 'rxjs/operators';

import { setLoadedProductDiscounts } from '../actions/prices.actions';

@Injectable()
export class MerchandisePricesEffects {
  private useMerchandiseForPrices$ = this.configService.getConfigValue({
    namespace: NAMESPACE_MERCHANDISE_PRICES,
    featureName: MerchandisePricesConfigurationKeys.UseMerchandiseForPrices,
    defaultValue: false,
  });

  private prices$ = this.useMerchandiseForPrices$.pipe(
    filter(Boolean),
    switchMap(() =>
      combineLatest([
        this.store.pipe(
          select(selectAccountId),
          filter(Boolean),
          distinctUntilChanged(),
        ),
        this.store.pipe(
          select(selectSiteId),
          filter(Boolean),
          distinctUntilChanged(),
        ),
      ]),
    ),
    throttleTime(1000),
    switchMap(([accountId, siteId]: [string, string]) =>
      this.merchandiseService.getMerchandisePrices(accountId, siteId),
    ),
  );

  public loadedProductDiscounts$ = createEffect(() =>
    this.prices$.pipe(
      map((merchandisePrices: MerchandisePrices[]) =>
        merchandisePrices.filter(
          (merchandisePrice: MerchandisePrices) =>
            (merchandisePrice?.value &&
              merchandisePrice?.value?.products?.length > 0) ??
            false,
        ),
      ),
      map((merchandisePrices) =>
        setLoadedProductDiscounts(
          merchandisePrices.reduce((acc, { slotId, value }) => {
            const discount = +slotId.split('-').slice(-1)[0];
            if (!discount) {
              return { ...acc };
            }
            const productIds = value?.products.map(
              ({ productId }) => productId,
            );
            return {
              ...acc,
              ...productIds?.reduce(
                (productIdsAcc, productIdsCurr) => ({
                  ...productIdsAcc,
                  [productIdsCurr]: discount,
                }),
                {},
              ),
            };
          }, {}),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private configService: ConfigurationsService,
    private merchandiseService: MerchandiseService,
  ) {}
}
